/*
  Support for generating report pipelines with AI.

  The frontend describes the tables it has (column types plus sample values) and the pipeline the user has built so
  far, the backend asks the model for operators to append, and the frontend runs the result against the real data.
  Any problems found that way are sent back so the model can fix its own mistakes.
*/

import type { CalendarType } from '../formulas/units.ts';
import {
  type ColumnID,
  type ColumnType,
  type ColumnValue,
  type FilterCompareValue,
  type PipelineOperatorAnnotated,
  type PipelineResult,
  type Table,
  type TableID,
  toPrimitive,
} from './report-pipelines.ts';

export type ReportAIColumn = {
  columnID: ColumnID;
  // Distinct values in order of first appearance, formatted for the model to read:
  sampleValues: string[];
  distinctValueCount: number;
} & (
  | { type: 'number'; units: string }
  | { type: 'calendarValue'; calendarType: CalendarType }
  | { type: Exclude<ColumnType, 'number' | 'calendarValue'> }
);

export interface ReportAITable {
  tableID: TableID;
  rowCount: number;
  columns: ReportAIColumn[];
}

export type ReportAIImageMediaType = 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';

export interface ReportAIImage {
  mediaType: ReportAIImageMediaType;
  base64: string;
}

export interface ReportAIGenerated {
  reportName: string;
  explanation: string;
  // Only the operators to append to the user's existing pipeline:
  pipelineOperators: PipelineOperatorAnnotated[];
}

export type ReportAIResponse =
  | { type: 'generated'; generated: ReportAIGenerated; responseText: string }
  // The model's response could not be understood:
  | { type: 'invalid'; problems: string[]; responseText: string };

export interface ReportAIAttempt {
  // The model's raw response, so that a retry shows the model exactly what it said:
  responseText: string;
  problems: string[];
}

const sampleLimits: Record<ColumnType, number> = {
  number: 5,
  string: 40,
  boolean: 2,
  productID: 10,
  formulaID: 10,
  releaseProfileID: 10,
  casOrCategory: 60,
  units: 20,
  calendarValue: 6,
};

/**
 * `valueLabel` gives a human-friendly rendering of a value (i.e. a chemical's name), which is shown alongside the raw
 * value when they differ so the model can both understand the value and reference it exactly.
 */
function sampleFormat(value: ColumnValue, valueLabel: (value: ColumnValue) => string): string {
  if (value.type === 'number') {
    const units = value.units.toString();
    return units ? `${value.value} ${units}` : String(value.value);
  }

  const raw = value.type === 'calendarValue' ? value.value.key : String(toPrimitive(value));
  const label = valueLabel(value);
  return label && label !== raw ? `${JSON.stringify(raw)} (${label})` : JSON.stringify(raw);
}

export function reportAITableDescribe(table: Table, valueLabel: (value: ColumnValue) => string): ReportAITable {
  const columns = Array.from(table.columns.values()).map((column): ReportAIColumn => {
    const samples = new Set<string>();
    for (const row of table.rows) {
      const value = row.values.get(column.columnID);
      if (value !== null && value !== undefined) {
        samples.add(sampleFormat(value, valueLabel));
      }
    }

    const base = {
      columnID: column.columnID,
      sampleValues: Array.from(samples.values()).slice(0, sampleLimits[column.type]),
      distinctValueCount: samples.size,
    };
    if (column.type === 'number') {
      return { ...base, type: 'number', units: column.units.toString() };
    } else if (column.type === 'calendarValue') {
      return { ...base, type: 'calendarValue', calendarType: column.calendarType };
    } else {
      return { ...base, type: column.type };
    }
  });

  return { tableID: table.tableID, rowCount: table.rows.length, columns };
}

export interface ReportAIQuickFilters {
  // Each list is empty when the user does not want to filter on that column:
  kinds: string[];
  units: string[];
  massOnly: boolean;
}

function quickFilterOperator(columnID: string, values: FilterCompareValue[]): PipelineOperatorAnnotated[] {
  if (values.length === 0) {
    return [];
  } else if (values.length === 1) {
    return [
      {
        notes: `Only include ${columnID} ${values[0]}`,
        config: { type: 'filterCompare', columnID: columnID as ColumnID, operator: '=', value: values[0] },
      },
    ];
  } else {
    return [
      {
        notes: `Only include ${columnID} ${values.join(', ')}`,
        config: { type: 'filterCompare', columnID: columnID as ColumnID, operator: 'in', value: values },
      },
    ];
  }
}

/** Filters applied before the model sees the data, so that common choices don't cost tokens or risk mistakes. */
export function reportAIQuickFilterOperators(filters: ReportAIQuickFilters): PipelineOperatorAnnotated[] {
  return [
    ...(filters.massOnly
      ? [
          {
            notes: 'Only include Mass results',
            config: {
              type: 'filterCompare',
              columnID: 'ResultDimensions' as ColumnID,
              operator: '=',
              value: 'Mass',
            },
          } satisfies PipelineOperatorAnnotated,
        ]
      : []),
    ...quickFilterOperator('Kind', filters.kinds),
    ...quickFilterOperator('Unit', filters.units),
  ];
}

/**
 * Problems with a generated pipeline that are worth asking the model to fix. An empty result is suspicious (i.e. a
 * filter on a misspelled value), but may be correct, so the model may insist on it by returning the same pipeline.
 */
export function reportAIProblemsFind(
  result: PipelineResult,
  inputRowCount: number,
  attempt: {
    pipelineOperators: PipelineOperatorAnnotated[];
    previousPipelineOperators: PipelineOperatorAnnotated[] | null;
  },
): string[] {
  if (result.type === 'failure') {
    return result.warnings.map((warning) => warning.message);
  }

  const problems = result.warnings.map((warning) => warning.message);
  const isRepeat = JSON.stringify(attempt.previousPipelineOperators) === JSON.stringify(attempt.pipelineOperators);
  if (inputRowCount > 0 && result.table.rows.length === 0 && !isRepeat) {
    problems.push(
      `The pipeline ran without errors but produced 0 rows (from ${inputRowCount} input rows). Check that filter ` +
        `values exactly match the sample values. If 0 rows is genuinely correct, return the same pipeline again.`,
    );
  }

  return problems;
}
